import { useMemo, useRef, useState } from 'react';
import type { KeyedMutator } from 'swr';
import useSWR from 'swr';
import { ArrowLeft, CreditCard, Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import axiosInstance from '@/lib/axios';
import { toast } from 'sonner';
import type { Community } from '@/types';
import type {
  CommunityPaymentAccount,
  MembershipCycleAnchor,
  MembershipFeeBucket,
  MembershipFeeComponent,
  MembershipFeeKind,
  MembershipFeePeriodicity,
  MembershipFeeStructure,
  MembershipInstallmentItem,
  MembershipInstallmentPackage,
  MembershipMemberType,
  MembershipMemberTypeKind,
  MembershipRenewalFeeMode,
} from '@/types/membership';
import {
  createClientMembershipId,
  CYCLE_ANCHOR_LABELS,
  FEE_KIND_LABELS,
  FEE_PERIODICITY_LABELS,
  formatMembershipPrice,
  getFeeBucketTotal,
  getInstallmentTotal,
  MEMBER_TYPE_LABELS,
  RENEWAL_FEE_MODE_LABELS,
} from '@/types/membership';

type CommunityMutate = KeyedMutator<{ community: Community }>;

type AccountDraft = Omit<CommunityPaymentAccount, 'id' | 'communityId'>;

type FeeStructureDraft = Omit<MembershipFeeStructure, 'id' | 'communityId'>;

const FEE_KIND_ORDER: MembershipFeeKind[] = ['REGISTRATION', 'MEMBERSHIP', 'RENEWAL', 'ONE_TIME'];

function emptyAccountDraft(type: 'BANK' | 'UPI' = 'BANK'): AccountDraft {
  return {
    name: '',
    type,
    isActive: true,
    beneficiaryName: '',
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    branchName: '',
    upiId: '',
    upiType: '',
    upiName: '',
    upiAccountNumber: '',
    upiQrCode: '',
  };
}

function emptyInstallmentItem(componentIds: string[]): MembershipInstallmentItem {
  return {
    id: createClientMembershipId(),
    name: 'Installment 1',
    installmentNumber: 1,
    payableStartDate: '',
    payableEndDate: '',
    componentSplits: componentIds.map((feeComponentId) => ({
      feeComponentId,
      amount: 0,
    })),
  };
}

function emptyFeeBucket(feeKind: MembershipFeeKind): MembershipFeeBucket {
  const componentId = createClientMembershipId();
  return {
    feeKind,
    isEnabled: feeKind === 'MEMBERSHIP',
    totalAmount: 0,
    sortOrder: FEE_KIND_ORDER.indexOf(feeKind),
    components: [
      {
        id: componentId,
        name: `${FEE_KIND_LABELS[feeKind]} component`,
        amount: 0,
        paymentAccountId: '',
        sortOrder: 0,
      },
    ],
    installmentPackages: [
      {
        id: createClientMembershipId(),
        name: 'Single payment',
        description: '',
        sortOrder: 0,
        isActive: true,
        installments: [emptyInstallmentItem([componentId])],
      },
    ],
  };
}

function emptyMemberType(): MembershipMemberType {
  return {
    id: createClientMembershipId(),
    name: '',
    feeType: 'INDIVIDUAL',
    renewalFeeMode: 'SAME_AS_MEMBERSHIP',
    sortOrder: 0,
    isActive: true,
    feeBuckets: FEE_KIND_ORDER.map((feeKind) => emptyFeeBucket(feeKind)),
  };
}

function emptyFeeStructureDraft(): FeeStructureDraft {
  return {
    name: '',
    applicableFrom: new Date().toISOString().slice(0, 10),
    isActive: true,
    feePeriodicity: 'YEARLY',
    cycleAnchor: 'JOIN_ANNIVERSARY',
    fixedCycleMonth: null,
    graceDays: 0,
    memberTypes: [emptyMemberType()],
  };
}

function isMembershipId(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.length === 10;
}

function serializeFeeStructureDraft(draft: FeeStructureDraft) {
  return {
    name: draft.name.trim(),
    applicableFrom: draft.applicableFrom,
    isActive: draft.isActive,
    feePeriodicity: draft.feePeriodicity ?? 'YEARLY',
    cycleAnchor: draft.cycleAnchor ?? 'JOIN_ANNIVERSARY',
    fixedCycleMonth: draft.cycleAnchor === 'FIXED_MONTH' ? (draft.fixedCycleMonth ?? 4) : null,
    graceDays: draft.graceDays ?? 0,
    memberTypes: draft.memberTypes.map((memberType, memberTypeIndex) => ({
      name: memberType.name.trim(),
      feeType: memberType.feeType,
      renewalFeeMode: memberType.renewalFeeMode ?? 'SAME_AS_MEMBERSHIP',
      sortOrder: memberTypeIndex,
      isActive: memberType.isActive,
      feeBuckets: memberType.feeBuckets.map((bucket, bucketIndex) => {
        if (!bucket.isEnabled) {
          return {
            feeKind: bucket.feeKind,
            isEnabled: false,
            totalAmount: 0,
            sortOrder: bucketIndex,
            components: [],
            installmentPackages: [],
          };
        }

        return {
          feeKind: bucket.feeKind,
          isEnabled: true,
          totalAmount: bucket.totalAmount,
          sortOrder: bucketIndex,
          components: bucket.components.map((component, componentIndex) => ({
            ...(isMembershipId(component.id) ? { id: component.id } : {}),
            name: component.name.trim(),
            amount: component.amount,
            paymentAccountId: component.paymentAccountId,
            sortOrder: componentIndex,
          })),
          installmentPackages: bucket.installmentPackages.map((pkg, packageIndex) => ({
            name: pkg.name.trim(),
            description: pkg.description?.trim() ? pkg.description.trim() : null,
            sortOrder: packageIndex,
            isActive: pkg.isActive ?? true,
            installments: pkg.installments.map((installment, installmentIndex) => ({
              name: installment.name.trim(),
              installmentNumber: installmentIndex + 1,
              payableStartDate: installment.payableStartDate?.trim()
                ? installment.payableStartDate
                : null,
              payableEndDate: installment.payableEndDate,
              componentSplits: installment.componentSplits
                .filter((split) => isMembershipId(split.feeComponentId))
                .map((split) => ({
                  feeComponentId: split.feeComponentId,
                  amount: split.amount,
                })),
            })),
          })),
        };
      }),
    })),
  };
}

function syncInstallmentSplits(
  packages: MembershipInstallmentPackage[],
  components: MembershipFeeComponent[]
): MembershipInstallmentPackage[] {
  return packages.map((pkg) => ({
    ...pkg,
    installments: pkg.installments.map((installment) => {
      const existingByComponentId = new Map(
        installment.componentSplits.map((split) => [split.feeComponentId, split])
      );

      return {
        ...installment,
        componentSplits: components.map((component) => {
          const existing = existingByComponentId.get(component.id);
          return {
            feeComponentId: component.id,
            amount: existing?.amount ?? 0,
            id: existing?.id,
            installmentItemId: existing?.installmentItemId ?? installment.id,
          };
        }),
      };
    }),
  }));
}

function parseNonNegativeInt(value: string): number {
  if (value.trim() === '') {
    return 0;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return 0;
  }
  return Math.round(parsed);
}

function formatAmountInputValue(value: number): string {
  return value === 0 ? '' : String(value);
}

function getPackageComponentAllocation(
  pkg: MembershipInstallmentPackage,
  feeComponentId: string
): number {
  return pkg.installments.reduce((total, installment) => {
    const split = installment.componentSplits.find(
      (item) => item.feeComponentId === feeComponentId
    );
    return total + (split?.amount ?? 0);
  }, 0);
}

function validateFeeStructureDraft(draft: FeeStructureDraft): string | null {
  if (!draft.name.trim()) {
    return 'Fee structure name is required';
  }
  if (!draft.applicableFrom) {
    return 'Applicable from date is required';
  }
  if (draft.memberTypes.length === 0) {
    return 'Add at least one member type';
  }

  for (const memberType of draft.memberTypes) {
    if (!memberType.name.trim()) {
      return 'Every member type needs a name';
    }

    const enabledBuckets = memberType.feeBuckets.filter((bucket) => bucket.isEnabled);
    const enabledKinds = enabledBuckets.map((bucket) => bucket.feeKind);

    if (
      enabledKinds.includes('ONE_TIME') &&
      (enabledKinds.includes('MEMBERSHIP') || enabledKinds.includes('RENEWAL'))
    ) {
      return `${memberType.name} cannot mix one-time fees with membership or renewal fees`;
    }

    for (const bucket of enabledBuckets) {
      if (bucket.components.length === 0) {
        return `${FEE_KIND_LABELS[bucket.feeKind]} for ${memberType.name} needs at least one component`;
      }

      const componentTotal = getFeeBucketTotal(bucket);
      if (componentTotal !== bucket.totalAmount) {
        return `${FEE_KIND_LABELS[bucket.feeKind]} for ${memberType.name}: component total (${componentTotal}) must equal declared total (${bucket.totalAmount})`;
      }

      if (
        !bucket.components.every((component) => component.name.trim() && component.paymentAccountId)
      ) {
        return `${FEE_KIND_LABELS[bucket.feeKind]} for ${memberType.name}: every component needs a name and payment account`;
      }

      for (const pkg of bucket.installmentPackages) {
        if (!pkg.name.trim()) {
          return `Installment package name is required in ${FEE_KIND_LABELS[bucket.feeKind]} for ${memberType.name}`;
        }
        if (pkg.installments.length === 0) {
          return `${pkg.name || 'Installment package'} needs at least one installment`;
        }

        for (const installment of pkg.installments) {
          if (!installment.name.trim()) {
            return `Installment name is required in ${pkg.name}`;
          }
          if (!installment.payableEndDate) {
            return `${installment.name || 'Installment'} needs a payable end date`;
          }
          if (
            installment.payableStartDate &&
            installment.payableStartDate > installment.payableEndDate
          ) {
            return `${installment.name} has an invalid payable window`;
          }
        }

        for (const component of bucket.components) {
          const allocated = getPackageComponentAllocation(pkg, component.id);
          if (allocated !== component.amount) {
            return `${pkg.name}: installment splits for "${component.name}" total ${allocated}, but component amount is ${component.amount}`;
          }
        }
      }
    }
  }

  return null;
}

function toStructureDraft(structure: MembershipFeeStructure): FeeStructureDraft {
  return {
    name: structure.name,
    applicableFrom: structure.applicableFrom.slice(0, 10),
    isActive: structure.isActive,
    feePeriodicity: structure.feePeriodicity ?? 'YEARLY',
    cycleAnchor: structure.cycleAnchor ?? 'JOIN_ANNIVERSARY',
    fixedCycleMonth: structure.fixedCycleMonth ?? null,
    graceDays: structure.graceDays ?? 0,
    memberTypes: structure.memberTypes.map((memberType) => ({
      ...memberType,
      renewalFeeMode: memberType.renewalFeeMode ?? 'SAME_AS_MEMBERSHIP',
      feeBuckets: memberType.feeBuckets.map((bucket) => ({
        ...bucket,
        installmentPackages: syncInstallmentSplits(
          bucket.installmentPackages.map((pkg) => ({
            ...pkg,
            description: pkg.description ?? '',
            installments: pkg.installments.map((installment) => ({
              ...installment,
              payableStartDate: installment.payableStartDate?.slice(0, 10) ?? '',
              payableEndDate: installment.payableEndDate.slice(0, 10),
            })),
          })),
          bucket.components
        ),
      })),
    })),
  };
}

function toAccountDraft(account: CommunityPaymentAccount): AccountDraft {
  return {
    name: account.name,
    type: account.type,
    isActive: account.isActive,
    beneficiaryName: account.beneficiaryName ?? '',
    bankName: account.bankName ?? '',
    accountNumber: account.accountNumber ?? '',
    ifscCode: account.ifscCode ?? '',
    branchName: account.branchName ?? '',
    upiId: account.upiId ?? '',
    upiType: account.upiType ?? '',
    upiName: account.upiName ?? '',
    upiAccountNumber: account.upiAccountNumber ?? '',
    upiQrCode: account.upiQrCode ?? '',
  };
}

interface MembershipSettingsProps {
  communityId: string;
  mutateCommunity: CommunityMutate;
  isMembershipManagementEnabled: boolean;
  setIsMembershipManagementEnabled: (val: boolean) => void;
}

type PaymentAccountsResponse = {
  paymentAccounts: CommunityPaymentAccount[];
};

type FeeStructuresResponse = {
  feeStructures: MembershipFeeStructure[];
};

export function MembershipSettings({
  communityId,
  mutateCommunity,
  isMembershipManagementEnabled,
  setIsMembershipManagementEnabled,
}: MembershipSettingsProps) {
  const [accountFormOpen, setAccountFormOpen] = useState(false);
  const [structureFormOpen, setStructureFormOpen] = useState(false);
  const [accountDraft, setAccountDraft] = useState<AccountDraft>(emptyAccountDraft());
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
  const [structureDraft, setStructureDraft] = useState<FeeStructureDraft>(emptyFeeStructureDraft());
  const [editingStructureId, setEditingStructureId] = useState<string | null>(null);
  const [isSavingAccount, setIsSavingAccount] = useState(false);
  const [isSavingStructure, setIsSavingStructure] = useState(false);
  const componentAmountAtFocusRef = useRef<Record<string, number>>({});

  const {
    data: accountsData,
    mutate: mutateAccounts,
    isLoading: accountsLoading,
  } = useSWR<PaymentAccountsResponse>(
    isMembershipManagementEnabled
      ? `/api/communities/${communityId}/membership/payment-accounts`
      : null,
    (url: string) => axiosInstance.get(url).then((res) => res.data)
  );
  const {
    data: structuresData,
    mutate: mutateStructures,
    isLoading: structuresLoading,
  } = useSWR<FeeStructuresResponse>(
    isMembershipManagementEnabled
      ? `/api/communities/${communityId}/membership/fee-structures`
      : null,
    (url: string) => axiosInstance.get(url).then((res) => res.data)
  );

  const paymentAccounts = useMemo(
    (): CommunityPaymentAccount[] => accountsData?.paymentAccounts ?? [],
    [accountsData?.paymentAccounts]
  );
  const feeStructures = useMemo(
    (): MembershipFeeStructure[] => structuresData?.feeStructures ?? [],
    [structuresData?.feeStructures]
  );

  const enabledAccounts = useMemo(
    (): CommunityPaymentAccount[] => paymentAccounts.filter((account) => account.isActive),
    [paymentAccounts]
  );

  const toggleFeature = async (enabled: boolean) => {
    try {
      await axiosInstance.patch(`/api/communities/${communityId}`, {
        isMembershipManagementEnabled: enabled,
      });
      setIsMembershipManagementEnabled(enabled);
      await mutateCommunity();
      toast.success(enabled ? 'Membership management enabled' : 'Membership management disabled');
    } catch {
      toast.error('Failed to update membership settings');
    }
  };

  const openCreateAccount = () => {
    setEditingAccountId(null);
    setAccountDraft(emptyAccountDraft());
    setAccountFormOpen(true);
  };

  const openEditAccount = (account: CommunityPaymentAccount) => {
    setEditingAccountId(account.id);
    setAccountDraft(toAccountDraft(account));
    setAccountFormOpen(true);
  };

  const closeAccountForm = () => {
    setAccountFormOpen(false);
    setEditingAccountId(null);
    setAccountDraft(emptyAccountDraft());
  };

  const openCreateStructure = () => {
    setEditingStructureId(null);
    setStructureDraft(emptyFeeStructureDraft());
    setStructureFormOpen(true);
  };

  const openEditStructure = (structure: MembershipFeeStructure) => {
    setEditingStructureId(structure.id);
    setStructureDraft(toStructureDraft(structure));
    setStructureFormOpen(true);
  };

  const closeStructureForm = () => {
    setStructureFormOpen(false);
    setEditingStructureId(null);
    setStructureDraft(emptyFeeStructureDraft());
  };

  const saveAccount = async () => {
    if (!accountDraft.name.trim()) {
      toast.error('Account name is required');
      return;
    }

    setIsSavingAccount(true);
    try {
      if (editingAccountId) {
        await axiosInstance.patch(
          `/api/communities/${communityId}/membership/payment-accounts/${editingAccountId}`,
          accountDraft
        );
        toast.success('Payment account updated');
      } else {
        await axiosInstance.post(
          `/api/communities/${communityId}/membership/payment-accounts`,
          accountDraft
        );
        toast.success('Payment account created');
      }
      closeAccountForm();
      await mutateAccounts();
    } catch (error) {
      toast.error(axiosErrorMessage(error, 'Failed to save payment account'));
    } finally {
      setIsSavingAccount(false);
    }
  };

  const saveStructure = async () => {
    if (enabledAccounts.length === 0) {
      toast.error('Create at least one active payment account first');
      return;
    }

    const validationError = validateFeeStructureDraft(structureDraft);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setIsSavingStructure(true);
    try {
      const payload = serializeFeeStructureDraft(structureDraft);
      if (editingStructureId) {
        await axiosInstance.patch(
          `/api/communities/${communityId}/membership/fee-structures/${editingStructureId}`,
          payload
        );
        toast.success('Fee structure updated');
      } else {
        await axiosInstance.post(
          `/api/communities/${communityId}/membership/fee-structures`,
          payload
        );
        toast.success('Fee structure created');
      }
      closeStructureForm();
      await mutateStructures();
    } catch (error) {
      toast.error(axiosErrorMessage(error, 'Failed to save fee structure'));
    } finally {
      setIsSavingStructure(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-primary" />
            Membership Management
          </CardTitle>
          <CardDescription>
            Configure payment accounts, effective-dated fee structures, member types, fee buckets,
            and installment schedules.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="membership-enabled">Enable membership management</Label>
              <p className="text-sm text-muted-foreground">
                Members will see membership fee structures and can declare manual payments.
              </p>
            </div>
            <Switch
              id="membership-enabled"
              checked={isMembershipManagementEnabled}
              onCheckedChange={toggleFeature}
            />
          </div>
        </CardContent>
      </Card>

      {isMembershipManagementEnabled && (
        <>
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
              <div className="space-y-1.5">
                <CardTitle>
                  {accountFormOpen
                    ? editingAccountId
                      ? 'Edit Payment Account'
                      : 'Add Payment Account'
                    : 'Payment Accounts'}
                </CardTitle>
                <CardDescription>
                  {accountFormOpen
                    ? 'Enter bank or UPI details for fee component collections.'
                    : 'Bank and UPI accounts that fee components will point to.'}
                </CardDescription>
              </div>
              {!accountFormOpen && (
                <Button onClick={openCreateAccount}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add account
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              {accountFormOpen ? (
                <>
                  <Button variant="ghost" size="sm" onClick={closeAccountForm} className="-ml-2">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to accounts
                  </Button>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Account name</Label>
                      <Input
                        value={accountDraft.name}
                        onChange={(e) =>
                          setAccountDraft((draft) => ({ ...draft, name: e.target.value }))
                        }
                        placeholder="Main collection account"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Account type</Label>
                      <Select
                        value={accountDraft.type}
                        onValueChange={(value) =>
                          setAccountDraft((draft) => ({
                            ...emptyAccountDraft(value as 'BANK' | 'UPI'),
                            name: draft.name,
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="BANK">Bank</SelectItem>
                          <SelectItem value="UPI">UPI</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {accountDraft.type === 'BANK' ? (
                    <div className="grid gap-4 md:grid-cols-2">
                      <Field
                        label="Beneficiary name"
                        value={accountDraft.beneficiaryName ?? ''}
                        onChange={(value) =>
                          setAccountDraft((draft) => ({ ...draft, beneficiaryName: value }))
                        }
                      />
                      <Field
                        label="Bank name"
                        value={accountDraft.bankName ?? ''}
                        onChange={(value) =>
                          setAccountDraft((draft) => ({ ...draft, bankName: value }))
                        }
                      />
                      <Field
                        label="Account number"
                        value={accountDraft.accountNumber ?? ''}
                        onChange={(value) =>
                          setAccountDraft((draft) => ({ ...draft, accountNumber: value }))
                        }
                      />
                      <Field
                        label="IFSC code"
                        value={accountDraft.ifscCode ?? ''}
                        onChange={(value) =>
                          setAccountDraft((draft) => ({ ...draft, ifscCode: value }))
                        }
                      />
                      <Field
                        label="Branch name"
                        value={accountDraft.branchName ?? ''}
                        onChange={(value) =>
                          setAccountDraft((draft) => ({ ...draft, branchName: value }))
                        }
                      />
                    </div>
                  ) : (
                    <div className="grid gap-4 md:grid-cols-2">
                      <Field
                        label="UPI ID"
                        value={accountDraft.upiId ?? ''}
                        onChange={(value) =>
                          setAccountDraft((draft) => ({ ...draft, upiId: value }))
                        }
                      />
                      <Field
                        label="UPI name"
                        value={accountDraft.upiName ?? ''}
                        onChange={(value) =>
                          setAccountDraft((draft) => ({ ...draft, upiName: value }))
                        }
                      />
                      <Field
                        label="UPI type"
                        value={accountDraft.upiType ?? ''}
                        onChange={(value) =>
                          setAccountDraft((draft) => ({ ...draft, upiType: value }))
                        }
                      />
                      <Field
                        label="UPI account number"
                        value={accountDraft.upiAccountNumber ?? ''}
                        onChange={(value) =>
                          setAccountDraft((draft) => ({ ...draft, upiAccountNumber: value }))
                        }
                      />
                      <div className="space-y-2 md:col-span-2">
                        <Label>QR code URL</Label>
                        <Input
                          value={accountDraft.upiQrCode ?? ''}
                          onChange={(e) =>
                            setAccountDraft((draft) => ({ ...draft, upiQrCode: e.target.value }))
                          }
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    <Switch
                      checked={accountDraft.isActive}
                      onCheckedChange={(value) =>
                        setAccountDraft((draft) => ({ ...draft, isActive: value }))
                      }
                    />
                    <span className="text-sm text-muted-foreground">Account is active</span>
                  </div>

                  <div className="flex gap-2">
                    <Button onClick={saveAccount} disabled={isSavingAccount}>
                      {isSavingAccount && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {editingAccountId ? 'Save changes' : 'Create account'}
                    </Button>
                    <Button variant="outline" onClick={closeAccountForm}>
                      Cancel
                    </Button>
                  </div>
                </>
              ) : accountsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : paymentAccounts.length === 0 ? (
                <div className="rounded-xl border border-dashed p-8 text-center space-y-3">
                  <p className="text-sm text-muted-foreground">No payment accounts yet.</p>
                  <Button onClick={openCreateAccount}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add your first account
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {paymentAccounts.map((account) => (
                    <div key={account.id} className="rounded-xl border p-4">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium">{account.name}</p>
                            <Badge variant="outline">{account.type}</Badge>
                            {!account.isActive && <Badge variant="secondary">Inactive</Badge>}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {account.type === 'BANK'
                              ? `${account.bankName ?? 'Bank'} • ${account.accountNumber ?? 'No account number'}`
                              : `${account.upiId ?? 'No UPI ID'}${account.upiName ? ` • ${account.upiName}` : ''}`}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditAccount(account)}
                        >
                          Edit
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
              <div className="space-y-1.5">
                <CardTitle>
                  {structureFormOpen
                    ? editingStructureId
                      ? 'Edit Fee Structure'
                      : 'Add Fee Structure'
                    : 'Fee Structures'}
                </CardTitle>
                <CardDescription>
                  {structureFormOpen
                    ? 'Define member types, payable buckets, fee components, and installment packages.'
                    : 'Effective-dated pricing structures for membership fees.'}
                </CardDescription>
              </div>
              {!structureFormOpen && (
                <Button onClick={openCreateStructure}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add fee structure
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              {structureFormOpen ? (
                <>
                  <Button variant="ghost" size="sm" onClick={closeStructureForm} className="-ml-2">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to fee structures
                  </Button>

                  <div className="grid gap-4 md:grid-cols-3">
                    <Field
                      label="Structure name"
                      value={structureDraft.name}
                      onChange={(value) =>
                        setStructureDraft((draft) => ({ ...draft, name: value }))
                      }
                    />
                    <div className="space-y-2">
                      <Label>Applicable from</Label>
                      <Input
                        type="date"
                        value={structureDraft.applicableFrom}
                        onChange={(e) =>
                          setStructureDraft((draft) => ({
                            ...draft,
                            applicableFrom: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="flex items-center justify-between rounded-xl border px-4 py-3">
                      <div>
                        <Label htmlFor="structure-active">Active</Label>
                        <p className="text-xs text-muted-foreground">
                          Inactive structures are hidden from members
                        </p>
                      </div>
                      <Switch
                        id="structure-active"
                        checked={structureDraft.isActive}
                        onCheckedChange={(checked) =>
                          setStructureDraft((draft) => ({ ...draft, isActive: checked }))
                        }
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Fee periodicity</Label>
                      <Select
                        value={structureDraft.feePeriodicity ?? 'YEARLY'}
                        onValueChange={(value) =>
                          setStructureDraft((draft) => ({
                            ...draft,
                            feePeriodicity: value as MembershipFeePeriodicity,
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(FEE_PERIODICITY_LABELS).map(([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Used to auto-calculate membership expiry and renewals.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label>Fee start</Label>
                      <Select
                        value={structureDraft.cycleAnchor ?? 'JOIN_ANNIVERSARY'}
                        onValueChange={(value) =>
                          setStructureDraft((draft) => ({
                            ...draft,
                            cycleAnchor: value as MembershipCycleAnchor,
                            fixedCycleMonth:
                              value === 'FIXED_MONTH' ? (draft.fixedCycleMonth ?? 4) : null,
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(CYCLE_ANCHOR_LABELS).map(([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {structureDraft.cycleAnchor === 'FIXED_MONTH' && (
                      <div className="space-y-2">
                        <Label>Cycle start month</Label>
                        <Select
                          value={String(structureDraft.fixedCycleMonth ?? 4)}
                          onValueChange={(value) =>
                            setStructureDraft((draft) => ({
                              ...draft,
                              fixedCycleMonth: Number(value),
                            }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[
                              'January',
                              'February',
                              'March',
                              'April',
                              'May',
                              'June',
                              'July',
                              'August',
                              'September',
                              'October',
                              'November',
                              'December',
                            ].map((month, index) => (
                              <SelectItem key={month} value={String(index + 1)}>
                                {month}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Grace days after expiry</Label>
                      <Input
                        type="number"
                        min={0}
                        value={structureDraft.graceDays ?? 0}
                        onChange={(e) =>
                          setStructureDraft((draft) => ({
                            ...draft,
                            graceDays: Math.max(0, Math.round(Number(e.target.value) || 0)),
                          }))
                        }
                      />
                    </div>
                  </div>

                  {structureDraft.memberTypes.map((memberType, memberTypeIndex) => (
                    <Card key={memberType.id} className="border-primary/15">
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center justify-between gap-3">
                          <span>Member Type {memberTypeIndex + 1}</span>
                          {structureDraft.memberTypes.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              onClick={() =>
                                setStructureDraft((draft) => ({
                                  ...draft,
                                  memberTypes: draft.memberTypes.filter(
                                    (_, index) => index !== memberTypeIndex
                                  ),
                                }))
                              }
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Remove
                            </Button>
                          )}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        <div className="grid gap-4 md:grid-cols-2">
                          <Field
                            label="Member type name"
                            value={memberType.name}
                            onChange={(value) =>
                              setStructureDraft((draft) => ({
                                ...draft,
                                memberTypes: draft.memberTypes.map((item, index) =>
                                  index === memberTypeIndex ? { ...item, name: value } : item
                                ),
                              }))
                            }
                          />
                          <div className="space-y-2">
                            <Label>Fee type</Label>
                            <Select
                              value={memberType.feeType}
                              onValueChange={(value) =>
                                setStructureDraft((draft) => ({
                                  ...draft,
                                  memberTypes: draft.memberTypes.map((item, index) =>
                                    index === memberTypeIndex
                                      ? { ...item, feeType: value as MembershipMemberTypeKind }
                                      : item
                                  ),
                                }))
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(MEMBER_TYPE_LABELS).map(([value, label]) => (
                                  <SelectItem key={value} value={value}>
                                    {label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2 md:col-span-2">
                            <Label>Renewal fee</Label>
                            <Select
                              value={memberType.renewalFeeMode ?? 'SAME_AS_MEMBERSHIP'}
                              onValueChange={(value) =>
                                setStructureDraft((draft) => ({
                                  ...draft,
                                  memberTypes: draft.memberTypes.map((item, index) =>
                                    index === memberTypeIndex
                                      ? {
                                          ...item,
                                          renewalFeeMode: value as MembershipRenewalFeeMode,
                                        }
                                      : item
                                  ),
                                }))
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(RENEWAL_FEE_MODE_LABELS).map(([value, label]) => (
                                  <SelectItem key={value} value={value}>
                                    {label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">
                              Same as membership fee excludes registration and copies the membership
                              amount into renewal.
                            </p>
                          </div>
                        </div>

                        {memberType.feeBuckets.map((bucket, bucketIndex) => {
                          const hideRenewalEditor =
                            bucket.feeKind === 'RENEWAL' &&
                            (memberType.renewalFeeMode ?? 'SAME_AS_MEMBERSHIP') ===
                              'SAME_AS_MEMBERSHIP';
                          const componentTotal = getFeeBucketTotal(bucket);
                          const totalsMatch = componentTotal === bucket.totalAmount;
                          const remainingToAllocate = Math.max(
                            0,
                            bucket.totalAmount - componentTotal
                          );

                          return (
                            <Card key={bucket.feeKind}>
                              <CardHeader>
                                <CardTitle className="text-base flex items-center justify-between">
                                  {FEE_KIND_LABELS[bucket.feeKind]}
                                  <Switch
                                    checked={
                                      hideRenewalEditor
                                        ? Boolean(
                                            memberType.feeBuckets.find(
                                              (item) => item.feeKind === 'MEMBERSHIP'
                                            )?.isEnabled
                                          )
                                        : bucket.isEnabled
                                    }
                                    disabled={hideRenewalEditor}
                                    onCheckedChange={(checked) =>
                                      setFeeBucketEnabled(
                                        memberTypeIndex,
                                        bucketIndex,
                                        bucket.feeKind,
                                        checked
                                      )
                                    }
                                  />
                                </CardTitle>
                              </CardHeader>
                              {(bucket.isEnabled || hideRenewalEditor) &&
                                hideRenewalEditor &&
                                Boolean(
                                  memberType.feeBuckets.find((item) => item.feeKind === 'MEMBERSHIP')
                                    ?.isEnabled
                                ) && (
                                <CardContent>
                                  <p className="text-sm text-muted-foreground">
                                    Renewal amount follows the membership fee automatically.
                                  </p>
                                </CardContent>
                              )}
                              {bucket.isEnabled && !hideRenewalEditor && (
                                <CardContent className="space-y-4">
                                  <div className="grid gap-4 md:grid-cols-2">
                                    <div className="space-y-2">
                                      <Label>Declared bucket total</Label>
                                      <AmountInput
                                        value={bucket.totalAmount}
                                        onChange={(nextValue) =>
                                          updateBucket(memberTypeIndex, bucketIndex, {
                                            totalAmount: nextValue,
                                          })
                                        }
                                      />
                                    </div>
                                    <div
                                      className={`rounded-xl p-4 ${
                                        totalsMatch
                                          ? 'bg-muted/30'
                                          : 'bg-destructive/10 border border-destructive/30'
                                      }`}
                                    >
                                      <p className="text-sm text-muted-foreground">
                                        Component total
                                      </p>
                                      <p className="text-lg font-semibold">
                                        {formatMembershipPrice(componentTotal)}
                                      </p>
                                      {!totalsMatch && (
                                        <p className="text-sm text-destructive mt-1">
                                          Must equal declared total (
                                          {formatMembershipPrice(bucket.totalAmount)}). Difference:{' '}
                                          {formatMembershipPrice(
                                            Math.abs(bucket.totalAmount - componentTotal)
                                          )}
                                        </p>
                                      )}
                                    </div>
                                  </div>

                                  <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                      <Label>Fee components</Label>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                          addComponent(
                                            memberTypeIndex,
                                            bucketIndex,
                                            remainingToAllocate
                                          )
                                        }
                                      >
                                        <Plus className="mr-2 h-4 w-4" />
                                        Add component
                                      </Button>
                                    </div>
                                    {bucket.components.map((component, componentIndex) => (
                                      <div
                                        key={component.id}
                                        className="grid gap-3 md:grid-cols-[1fr_1fr_1fr_auto]"
                                      >
                                        <Field
                                          label="Component name"
                                          value={component.name}
                                          onChange={(value) =>
                                            updateComponent(
                                              memberTypeIndex,
                                              bucketIndex,
                                              componentIndex,
                                              { name: value }
                                            )
                                          }
                                        />
                                        <div className="space-y-2">
                                          <Label>Amount</Label>
                                          <AmountInput
                                            value={component.amount}
                                            onFocus={() => {
                                              componentAmountAtFocusRef.current[component.id] =
                                                component.amount;
                                            }}
                                            onChange={(nextValue) =>
                                              updateComponent(
                                                memberTypeIndex,
                                                bucketIndex,
                                                componentIndex,
                                                { amount: nextValue }
                                              )
                                            }
                                            onBlur={(nextValue) =>
                                              maybeCreateResidualComponent(
                                                memberTypeIndex,
                                                bucketIndex,
                                                componentIndex,
                                                component.id,
                                                nextValue
                                              )
                                            }
                                          />
                                        </div>
                                        <div className="space-y-2">
                                          <Label>Payment account</Label>
                                          <Select
                                            value={component.paymentAccountId}
                                            onValueChange={(value) =>
                                              updateComponent(
                                                memberTypeIndex,
                                                bucketIndex,
                                                componentIndex,
                                                { paymentAccountId: value }
                                              )
                                            }
                                          >
                                            <SelectTrigger>
                                              <SelectValue placeholder="Select account" />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {enabledAccounts.map((account) => (
                                                <SelectItem key={account.id} value={account.id}>
                                                  {account.name}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </div>
                                        <div className="flex items-end">
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="text-destructive hover:text-destructive"
                                            disabled={bucket.components.length <= 1}
                                            onClick={() =>
                                              removeComponent(
                                                memberTypeIndex,
                                                bucketIndex,
                                                componentIndex
                                              )
                                            }
                                          >
                                            <Trash2 className="h-4 w-4" />
                                          </Button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>

                                  <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                      <Label>Installment packages</Label>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                          updateBucket(memberTypeIndex, bucketIndex, {
                                            installmentPackages: [
                                              ...bucket.installmentPackages,
                                              {
                                                id: createClientMembershipId(),
                                                name: 'Custom package',
                                                description: '',
                                                sortOrder: bucket.installmentPackages.length,
                                                isActive: true,
                                                installments: [
                                                  emptyInstallmentItem(
                                                    bucket.components.map((item) => item.id)
                                                  ),
                                                ],
                                              },
                                            ],
                                          })
                                        }
                                      >
                                        <RefreshCw className="mr-2 h-4 w-4" />
                                        Add package
                                      </Button>
                                    </div>

                                    {bucket.installmentPackages.map((pkg, packageIndex) => (
                                      <Card key={pkg.id} className="bg-muted/20">
                                        <CardContent className="pt-6 space-y-4">
                                          <div className="flex items-start justify-between gap-3">
                                            <div className="grid flex-1 gap-4 md:grid-cols-2">
                                              <Field
                                                label="Package name"
                                                value={pkg.name}
                                                onChange={(value) =>
                                                  updatePackage(
                                                    memberTypeIndex,
                                                    bucketIndex,
                                                    packageIndex,
                                                    { name: value }
                                                  )
                                                }
                                              />
                                              <div className="space-y-2">
                                                <Label>Description</Label>
                                                <Textarea
                                                  value={pkg.description ?? ''}
                                                  onChange={(e) =>
                                                    updatePackage(
                                                      memberTypeIndex,
                                                      bucketIndex,
                                                      packageIndex,
                                                      { description: e.target.value }
                                                    )
                                                  }
                                                  rows={2}
                                                />
                                              </div>
                                            </div>
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="icon"
                                              className="text-destructive hover:text-destructive shrink-0"
                                              onClick={() =>
                                                removePackage(
                                                  memberTypeIndex,
                                                  bucketIndex,
                                                  packageIndex
                                                )
                                              }
                                            >
                                              <Trash2 className="h-4 w-4" />
                                            </Button>
                                          </div>

                                          <div className="space-y-2">
                                            {bucket.components.map((component) => {
                                              const allocated = getPackageComponentAllocation(
                                                pkg,
                                                component.id
                                              );
                                              const matches = allocated === component.amount;
                                              return (
                                                <p
                                                  key={component.id}
                                                  className={`text-sm ${
                                                    matches
                                                      ? 'text-muted-foreground'
                                                      : 'text-destructive'
                                                  }`}
                                                >
                                                  {component.name}: allocated{' '}
                                                  {formatMembershipPrice(allocated)} /{' '}
                                                  {formatMembershipPrice(component.amount)}
                                                  {!matches && ' (must match component amount)'}
                                                </p>
                                              );
                                            })}
                                          </div>

                                          {pkg.installments.map((installment, installmentIndex) => (
                                            <div
                                              key={installment.id}
                                              className="rounded-xl border p-4 space-y-4"
                                            >
                                              <div className="flex items-start justify-between gap-3">
                                                <div className="grid flex-1 gap-4 md:grid-cols-3">
                                                  <Field
                                                    label="Installment name"
                                                    value={installment.name}
                                                    onChange={(value) =>
                                                      updateInstallment(
                                                        memberTypeIndex,
                                                        bucketIndex,
                                                        packageIndex,
                                                        installmentIndex,
                                                        { name: value }
                                                      )
                                                    }
                                                  />
                                                  <div className="space-y-2">
                                                    <Label>Payable start date</Label>
                                                    <Input
                                                      type="date"
                                                      value={installment.payableStartDate ?? ''}
                                                      onChange={(e) =>
                                                        updateInstallment(
                                                          memberTypeIndex,
                                                          bucketIndex,
                                                          packageIndex,
                                                          installmentIndex,
                                                          { payableStartDate: e.target.value }
                                                        )
                                                      }
                                                    />
                                                  </div>
                                                  <div className="space-y-2">
                                                    <Label>Payable end date</Label>
                                                    <Input
                                                      type="date"
                                                      value={installment.payableEndDate}
                                                      onChange={(e) =>
                                                        updateInstallment(
                                                          memberTypeIndex,
                                                          bucketIndex,
                                                          packageIndex,
                                                          installmentIndex,
                                                          { payableEndDate: e.target.value }
                                                        )
                                                      }
                                                    />
                                                  </div>
                                                </div>
                                                <Button
                                                  type="button"
                                                  variant="ghost"
                                                  size="icon"
                                                  className="text-destructive hover:text-destructive shrink-0"
                                                  disabled={pkg.installments.length <= 1}
                                                  onClick={() =>
                                                    removeInstallment(
                                                      memberTypeIndex,
                                                      bucketIndex,
                                                      packageIndex,
                                                      installmentIndex
                                                    )
                                                  }
                                                >
                                                  <Trash2 className="h-4 w-4" />
                                                </Button>
                                              </div>

                                              <div className="space-y-2">
                                                <p className="text-sm font-medium">
                                                  Installment total:{' '}
                                                  {formatMembershipPrice(
                                                    getInstallmentTotal(installment)
                                                  )}
                                                </p>
                                                {installment.componentSplits.map(
                                                  (split, splitIndex) => {
                                                    const component = bucket.components.find(
                                                      (item) => item.id === split.feeComponentId
                                                    );
                                                    return (
                                                      <div
                                                        key={`${split.feeComponentId}-${splitIndex}`}
                                                        className="grid gap-3 md:grid-cols-[1fr_180px]"
                                                      >
                                                        <div className="rounded-lg border px-3 py-2 text-sm">
                                                          {component?.name ?? 'Unknown component'}
                                                        </div>
                                                        <AmountInput
                                                          value={split.amount}
                                                          onChange={(nextValue) =>
                                                            updateSplit(
                                                              memberTypeIndex,
                                                              bucketIndex,
                                                              packageIndex,
                                                              installmentIndex,
                                                              splitIndex,
                                                              nextValue
                                                            )
                                                          }
                                                        />
                                                      </div>
                                                    );
                                                  }
                                                )}
                                              </div>
                                            </div>
                                          ))}

                                          <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() =>
                                              addInstallment(
                                                memberTypeIndex,
                                                bucketIndex,
                                                packageIndex
                                              )
                                            }
                                          >
                                            <Plus className="mr-2 h-4 w-4" />
                                            Add installment
                                          </Button>
                                        </CardContent>
                                      </Card>
                                    ))}
                                  </div>
                                </CardContent>
                              )}
                            </Card>
                          );
                        })}
                      </CardContent>
                    </Card>
                  ))}

                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      onClick={() =>
                        setStructureDraft((draft) => ({
                          ...draft,
                          memberTypes: [
                            ...draft.memberTypes,
                            {
                              ...emptyMemberType(),
                              sortOrder: draft.memberTypes.length,
                            },
                          ],
                        }))
                      }
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add member type
                    </Button>
                    <Button onClick={saveStructure} disabled={isSavingStructure}>
                      {isSavingStructure && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {editingStructureId ? 'Save changes' : 'Create fee structure'}
                    </Button>
                    <Button variant="outline" onClick={closeStructureForm}>
                      Cancel
                    </Button>
                  </div>
                </>
              ) : structuresLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : feeStructures.length === 0 ? (
                <div className="rounded-xl border border-dashed p-8 text-center space-y-3">
                  <p className="text-sm text-muted-foreground">No fee structures yet.</p>
                  <Button onClick={openCreateStructure}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add your first fee structure
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {feeStructures.map((structure) => (
                    <div key={structure.id} className="rounded-xl border p-4 space-y-3">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold">{structure.name}</p>
                            <Badge variant="outline">
                              From {structure.applicableFrom.slice(0, 10)}
                            </Badge>
                            {structure.feePeriodicity && (
                              <Badge variant="secondary">
                                {FEE_PERIODICITY_LABELS[structure.feePeriodicity]}
                              </Badge>
                            )}
                            {!structure.isActive && <Badge variant="secondary">Inactive</Badge>}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {structure.memberTypes.length} member type
                            {structure.memberTypes.length === 1 ? '' : 's'}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditStructure(structure)}
                        >
                          Edit
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {structure.memberTypes.map((memberType) => (
                          <Badge key={memberType.id} variant="secondary">
                            {memberType.name || MEMBER_TYPE_LABELS[memberType.feeType]}
                            {memberType.feeBuckets.some((bucket) => bucket.isEnabled)
                              ? ` · ${memberType.feeBuckets
                                  .filter((bucket) => bucket.isEnabled)
                                  .map((bucket) => FEE_KIND_LABELS[bucket.feeKind])
                                  .join(', ')}`
                              : ''}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );

  function mapBucket(
    draft: FeeStructureDraft,
    memberTypeIndex: number,
    bucketIndex: number,
    updater: (bucket: MembershipFeeBucket) => MembershipFeeBucket
  ): FeeStructureDraft {
    return {
      ...draft,
      memberTypes: draft.memberTypes.map((memberType, itemIndex) =>
        itemIndex === memberTypeIndex
          ? {
              ...memberType,
              feeBuckets: memberType.feeBuckets.map((bucket, feeIndex) =>
                feeIndex === bucketIndex ? updater(bucket) : bucket
              ),
            }
          : memberType
      ),
    };
  }

  function updateBucket(
    memberTypeIndex: number,
    bucketIndex: number,
    patch: Partial<MembershipFeeBucket>
  ) {
    setStructureDraft((draft) =>
      mapBucket(draft, memberTypeIndex, bucketIndex, (bucket) => {
        const next = { ...bucket, ...patch };
        if (patch.components) {
          next.installmentPackages = syncInstallmentSplits(
            next.installmentPackages,
            next.components
          );
        }
        return next;
      })
    );
  }

  function setFeeBucketEnabled(
    memberTypeIndex: number,
    bucketIndex: number,
    feeKind: MembershipFeeKind,
    checked: boolean
  ) {
    setStructureDraft((draft) => ({
      ...draft,
      memberTypes: draft.memberTypes.map((memberType, itemIndex) => {
        if (itemIndex !== memberTypeIndex) {
          return memberType;
        }

        return {
          ...memberType,
          feeBuckets: memberType.feeBuckets.map((feeBucket, feeIndex) => {
            if (feeIndex === bucketIndex) {
              return { ...feeBucket, isEnabled: checked };
            }

            if (!checked) {
              return feeBucket;
            }

            if (
              feeKind === 'ONE_TIME' &&
              (feeBucket.feeKind === 'MEMBERSHIP' || feeBucket.feeKind === 'RENEWAL')
            ) {
              return { ...feeBucket, isEnabled: false };
            }

            if (
              (feeKind === 'MEMBERSHIP' || feeKind === 'RENEWAL') &&
              feeBucket.feeKind === 'ONE_TIME'
            ) {
              return { ...feeBucket, isEnabled: false };
            }

            return feeBucket;
          }),
        };
      }),
    }));
  }

  function addComponent(memberTypeIndex: number, bucketIndex: number, remainingToAllocate: number) {
    setStructureDraft((draft) =>
      mapBucket(draft, memberTypeIndex, bucketIndex, (bucket) => {
        const components = [
          ...bucket.components,
          {
            id: createClientMembershipId(),
            name: 'New component',
            amount: remainingToAllocate,
            paymentAccountId: enabledAccounts[0]?.id ?? '',
            sortOrder: bucket.components.length,
          },
        ];
        return {
          ...bucket,
          components,
          installmentPackages: syncInstallmentSplits(bucket.installmentPackages, components),
        };
      })
    );
  }

  function removeComponent(memberTypeIndex: number, bucketIndex: number, componentIndex: number) {
    setStructureDraft((draft) =>
      mapBucket(draft, memberTypeIndex, bucketIndex, (bucket) => {
        if (bucket.components.length <= 1) {
          return bucket;
        }
        const components = bucket.components.filter((_, index) => index !== componentIndex);
        return {
          ...bucket,
          components,
          installmentPackages: syncInstallmentSplits(bucket.installmentPackages, components),
        };
      })
    );
  }

  function updateComponent(
    memberTypeIndex: number,
    bucketIndex: number,
    componentIndex: number,
    patch: Partial<MembershipFeeComponent>
  ) {
    setStructureDraft((draft) =>
      mapBucket(draft, memberTypeIndex, bucketIndex, (bucket) => ({
        ...bucket,
        components: bucket.components.map((component, currentIndex) =>
          currentIndex === componentIndex ? { ...component, ...patch } : component
        ),
      }))
    );
  }

  function maybeCreateResidualComponent(
    memberTypeIndex: number,
    bucketIndex: number,
    componentIndex: number,
    componentId: string,
    nextAmount: number
  ) {
    const previousAmount = componentAmountAtFocusRef.current[componentId];
    delete componentAmountAtFocusRef.current[componentId];

    if (previousAmount == null || nextAmount >= previousAmount) {
      return;
    }

    const residualAmount = previousAmount - nextAmount;
    if (residualAmount <= 0) {
      return;
    }

    setStructureDraft((draft) =>
      mapBucket(draft, memberTypeIndex, bucketIndex, (bucket) => {
        const current = bucket.components[componentIndex];
        if (!current || current.id !== componentId) {
          return bucket;
        }

        const components = [
          ...bucket.components.map((component, currentIndex) =>
            currentIndex === componentIndex ? { ...component, amount: nextAmount } : component
          ),
          {
            id: createClientMembershipId(),
            name: `${current.name || 'Component'} residual`,
            amount: residualAmount,
            paymentAccountId: current.paymentAccountId || enabledAccounts[0]?.id || '',
            sortOrder: bucket.components.length,
          },
        ];

        return {
          ...bucket,
          components,
          installmentPackages: syncInstallmentSplits(bucket.installmentPackages, components),
        };
      })
    );
  }

  function updatePackage(
    memberTypeIndex: number,
    bucketIndex: number,
    packageIndex: number,
    patch: Partial<MembershipInstallmentPackage>
  ) {
    setStructureDraft((draft) =>
      mapBucket(draft, memberTypeIndex, bucketIndex, (bucket) => ({
        ...bucket,
        installmentPackages: bucket.installmentPackages.map((pkg, currentIndex) =>
          currentIndex === packageIndex ? { ...pkg, ...patch } : pkg
        ),
      }))
    );
  }

  function removePackage(memberTypeIndex: number, bucketIndex: number, packageIndex: number) {
    setStructureDraft((draft) =>
      mapBucket(draft, memberTypeIndex, bucketIndex, (bucket) => ({
        ...bucket,
        installmentPackages: bucket.installmentPackages.filter(
          (_, currentIndex) => currentIndex !== packageIndex
        ),
      }))
    );
  }

  function addInstallment(memberTypeIndex: number, bucketIndex: number, packageIndex: number) {
    setStructureDraft((draft) =>
      mapBucket(draft, memberTypeIndex, bucketIndex, (bucket) => ({
        ...bucket,
        installmentPackages: bucket.installmentPackages.map((pkg, currentIndex) => {
          if (currentIndex !== packageIndex) {
            return pkg;
          }
          const nextNumber = pkg.installments.length + 1;
          return {
            ...pkg,
            installments: [
              ...pkg.installments,
              {
                ...emptyInstallmentItem(bucket.components.map((component) => component.id)),
                name: `Installment ${nextNumber}`,
                installmentNumber: nextNumber,
              },
            ],
          };
        }),
      }))
    );
  }

  function removeInstallment(
    memberTypeIndex: number,
    bucketIndex: number,
    packageIndex: number,
    installmentIndex: number
  ) {
    setStructureDraft((draft) =>
      mapBucket(draft, memberTypeIndex, bucketIndex, (bucket) => ({
        ...bucket,
        installmentPackages: bucket.installmentPackages.map((pkg, currentIndex) => {
          if (currentIndex !== packageIndex || pkg.installments.length <= 1) {
            return pkg;
          }
          return {
            ...pkg,
            installments: pkg.installments
              .filter((_, currentInstallmentIndex) => currentInstallmentIndex !== installmentIndex)
              .map((installment, index) => ({
                ...installment,
                installmentNumber: index + 1,
              })),
          };
        }),
      }))
    );
  }

  function updateInstallment(
    memberTypeIndex: number,
    bucketIndex: number,
    packageIndex: number,
    installmentIndex: number,
    patch: Partial<MembershipInstallmentItem>
  ) {
    setStructureDraft((draft) =>
      mapBucket(draft, memberTypeIndex, bucketIndex, (bucket) => ({
        ...bucket,
        installmentPackages: bucket.installmentPackages.map((pkg, currentIndex) =>
          currentIndex === packageIndex
            ? {
                ...pkg,
                installments: pkg.installments.map((installment, currentInstallmentIndex) =>
                  currentInstallmentIndex === installmentIndex
                    ? { ...installment, ...patch }
                    : installment
                ),
              }
            : pkg
        ),
      }))
    );
  }

  function updateSplit(
    memberTypeIndex: number,
    bucketIndex: number,
    packageIndex: number,
    installmentIndex: number,
    splitIndex: number,
    amount: number
  ) {
    setStructureDraft((draft) =>
      mapBucket(draft, memberTypeIndex, bucketIndex, (bucket) => ({
        ...bucket,
        installmentPackages: bucket.installmentPackages.map((pkg, currentIndex) =>
          currentIndex === packageIndex
            ? {
                ...pkg,
                installments: pkg.installments.map((installment, currentInstallmentIndex) =>
                  currentInstallmentIndex === installmentIndex
                    ? {
                        ...installment,
                        componentSplits: installment.componentSplits.map(
                          (split, currentSplitIndex) =>
                            currentSplitIndex === splitIndex ? { ...split, amount } : split
                        ),
                      }
                    : installment
                ),
              }
            : pkg
        ),
      }))
    );
  }
}

function Field({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function AmountInput({
  value,
  onChange,
  onFocus,
  onBlur,
  min = 0,
}: {
  value: number;
  onChange: (value: number, raw: string) => void;
  onFocus?: () => void;
  onBlur?: (value: number, raw: string) => void;
  min?: number;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const displayValue = draft ?? formatAmountInputValue(value);

  return (
    <Input
      type="number"
      min={min}
      inputMode="numeric"
      placeholder="0"
      value={displayValue}
      onFocus={() => {
        setDraft(formatAmountInputValue(value));
        onFocus?.();
      }}
      onChange={(e) => {
        const nextRaw = e.target.value;
        setDraft(nextRaw);
        onChange(parseNonNegativeInt(nextRaw), nextRaw);
      }}
      onBlur={(e) => {
        const nextRaw = e.target.value;
        const nextValue = parseNonNegativeInt(nextRaw);
        setDraft(null);
        onBlur?.(nextValue, nextRaw);
      }}
    />
  );
}

function axiosErrorMessage(error: unknown, fallback: string) {
  const maybeResponse = error as { response?: { data?: { error?: string } } };
  return maybeResponse.response?.data?.error ?? fallback;
}
